import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

export class AuthErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Check if it's an auth-related error
    if (error.message.includes('session') || error.message.includes('Auth') || error.message.includes('Authentication')) {
      return { hasError: true, error };
    }
    return { hasError: false, error: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (error.message.includes('session') || error.message.includes('Auth') || error.message.includes('Authentication')) {
      console.error('Auth error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return <AuthErrorFallback />;
    }

    return this.props.children;
  }
}

function AuthErrorFallback() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  
  React.useEffect(() => {
    const handleAuthError = async () => {
      try {
        await signOut();
        // Navigation is now handled by signOut, but keep as fallback
        navigate('/auth', { replace: true });
      } catch (error) {
        console.error('Auth error boundary sign-out failed:', error);
        // Force navigation even if sign-out fails
        navigate('/auth', { replace: true });
      }
    };
    
    handleAuthError();
  }, [navigate, signOut]);
  
  return (
    <div className="flex justify-center items-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-sm text-muted-foreground">Session expired. Redirecting to login...</p>
      </div>
    </div>
  );
}
